/**
 * Submission Marking Page
 * Two-pane interface for marking student submissions
 * Left: Student work viewer | Right: Marking interface
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, Alert, CircularProgress,
  Breadcrumbs, Link as MuiLink, FormControl, InputLabel,
  Select, MenuItem, TextField, Divider, Card, CardContent,
  IconButton, Collapse, Stack, Tooltip
} from '@mui/material';
import {
  ChevronLeft, ChevronRight, Home, Description,
  CheckCircle, PlayArrow, ExpandMore, ExpandLess,
  NavigateBefore, NavigateNext, List, CloudUpload
} from '@mui/icons-material';
import axios from 'axios';
import { Submission } from '../../types/submission';
import { Rubric } from '../../types/rubric';
import SubmissionContentViewer from '../viewers/SubmissionContentViewer';
import StructuredFeedback from '../shared/StructuredFeedback';
import EditableStructuredFeedback from '../shared/EditableStructuredFeedback';
import { CodeSearchProvider } from '../../contexts/CodeSearchContext';
import { useAuth } from '../../contexts/AuthContext';
import { useImpersonation } from '../../contexts/ImpersonationContext';

interface AIMarkResponse {
  success: boolean;
  submission_id?: number;
  ai_score?: number;
  ai_feedback?: string;
  detailed_scores?: Record<string, any>;
  error?: string;
}

const SubmissionMarkingPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { session } = useImpersonation();

  // When impersonating a learner, treat the role as 'student'
  const userRole = session.is_impersonating ? 'student' : user?.user_profile?.role;

  // Data state
  const [submission, setSubmission] = useState<Submission | null>(null);
  const [rubrics, setRubrics] = useState<Rubric[]>([]);
  const [selectedRubric, setSelectedRubric] = useState<number | ''>('');

  // Navigation context - for seamless marking workflow
  const [submissionsList, setSubmissionsList] = useState<number[]>([]);
  const [currentIndex, setCurrentIndex] = useState<number>(-1);

  // UI state
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [markingPanelCollapsed, setMarkingPanelCollapsed] = useState(false);
  const [aiResultExpanded, setAiResultExpanded] = useState(true);
  const [replacing, setReplacing] = useState(false);
  const [replacingText, setReplacingText] = useState(false);
  const [replacementText, setReplacementText] = useState('');

  // Results state
  const [aiResult, setAiResult] = useState<AIMarkResponse | null>(null);
  const [teacherScore, setTeacherScore] = useState<string>('');
  const [teacherFeedback, setTeacherFeedback] = useState<string>('');
  const [teacherStructuredData, setTeacherStructuredData] = useState<any>(null);

  // Load submission and rubrics
  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;

      try {
        setLoading(true);

        // Reset teacher feedback + AI result state when switching submissions
        setTeacherScore('');
        setTeacherFeedback('');
        setTeacherStructuredData(null);
        setAiResult(null);

        // Fetch submission and rubrics in parallel
        const [submissionRes, rubricsRes] = await Promise.all([
          axios.get<Submission>(`/api/submissions/${id}/`),
          axios.get<{ results: Rubric[] }>('/api/rubrics/')
        ]);

        const submissionData = submissionRes.data;
        setSubmission(submissionData);

        // Debug: Log rubrics data
        console.log('[SubmissionMarkingPage] Rubrics response:', rubricsRes.data);
        console.log('[SubmissionMarkingPage] Rubrics results:', rubricsRes.data.results);

        const rubricsList = rubricsRes.data.results || [];
        console.log('[SubmissionMarkingPage] Setting rubrics:', rubricsList);
        console.log('[SubmissionMarkingPage] Rubrics count:', rubricsList.length);
        setRubrics(rubricsList);

        // Load last used rubric from localStorage and set as default if it exists
        const lastUsedRubricId = localStorage.getItem('lastUsedRubricId');
        if (lastUsedRubricId && rubricsList.some(r => r.id === Number(lastUsedRubricId))) {
          setSelectedRubric(Number(lastUsedRubricId));
        }

        // Set initial teacher score/feedback if available
        if (submissionData.teacher_score !== null) {
          setTeacherScore(String(submissionData.teacher_score));
        }
        if (submissionData.teacher_feedback) {
          setTeacherFeedback(submissionData.teacher_feedback);

          // Try to parse as structured data
          try {
            const parsedData = JSON.parse(submissionData.teacher_feedback);
            if (parsedData.criteria && Array.isArray(parsedData.criteria)) {
              setTeacherStructuredData(parsedData);
            }
          } catch (e) {
            // Not JSON - will use plain text fields
          }
        }
      } catch (err: any) {
        console.error('Error loading data:', err);
        setError('Failed to load submission data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // Load submission list for navigation (if context params are present)
  useEffect(() => {
    const fetchSubmissionsList = async () => {
      // Only fetch if we have filter context from URL params
      const classId = searchParams.get('class');
      const assignmentId = searchParams.get('assignment');
      const learnerId = searchParams.get('learner');
      const status = searchParams.get('status');

      if (!classId && !assignmentId && !learnerId && !status) {
        // No filter context, skip navigation setup
        return;
      }

      try {
        // Build API query params matching the backend SubmissionFilter (assessment, learner, status).
        // Class is not a backend filter — filter client-side via submission.learner_classes, mirroring SubmissionsPage.
        const apiParams = new URLSearchParams();
        if (assignmentId) apiParams.set('assessment', assignmentId);
        if (learnerId) apiParams.set('learner', learnerId);
        if (status) apiParams.set('status', status);
        apiParams.set('page_size', '1000'); // Get all matching submissions

        const response = await axios.get(`/api/submissions/?${apiParams.toString()}`);
        let submissions = response.data.results || [];

        if (classId) {
          const classIdNum = Number(classId);
          submissions = submissions.filter((s: any) =>
            Array.isArray(s.learner_classes) &&
            s.learner_classes.some((c: any) => c.id === classIdNum)
          );
        }

        const submissionIds = submissions.map((s: any) => s.id);

        setSubmissionsList(submissionIds);

        // Find current submission's index
        const currentId = Number(id);
        const index = submissionIds.indexOf(currentId);
        setCurrentIndex(index);
      } catch (error) {
        console.error('Error fetching submissions list for navigation:', error);
      }
    };

    fetchSubmissionsList();
  }, [id, searchParams]);

  const handleMarkWithAI = async () => {
    if (!submission || !selectedRubric) {
      setError('Please select a rubric');
      return;
    }

    // Save the selected rubric to localStorage for future use
    localStorage.setItem('lastUsedRubricId', String(selectedRubric));

    setMarking(true);
    setError(null);
    setAiResult(null);

    try {
      const response = await axios.post<AIMarkResponse>('/api/ai-mark/', {
        submission_id: submission.id,
        rubric_id: Number(selectedRubric)
      });

      setAiResult(response.data);

      // Reload submission to get updated AI scores
      const submissionRes = await axios.get<Submission>(`/api/submissions/${id}/`);
      setSubmission(submissionRes.data);
    } catch (err: any) {
      console.error('Error marking submission:', err);
      const errorMsg = err.response?.data?.error || 'Failed to mark submission';
      setError(errorMsg);
    } finally {
      setMarking(false);
    }
  };

  const handleSaveTeacherGrade = async () => {
    if (!submission) return;

    try {
      await axios.patch(`/api/submissions/${submission.id}/`, {
        teacher_score: teacherScore ? Number(teacherScore) : null,
        teacher_feedback: teacherFeedback || null,
        status: 'marked'
      });

      // Reload submission
      const submissionRes = await axios.get<Submission>(`/api/submissions/${id}/`);
      setSubmission(submissionRes.data);

      alert('Grade saved successfully!');
    } catch (err: any) {
      console.error('Error saving grade:', err);
      setError('Failed to save grade');
    }
  };

  const handleTeacherDataChange = (newData: any) => {
    setTeacherStructuredData(newData);
    setTeacherScore(String(newData.score));
    setTeacherFeedback(JSON.stringify(newData));
  };

  const handleCopyFromAI = () => {
    if (submission && submission.ai_feedback) {
      try {
        // Try to parse as JSON structured data
        const parsedData = JSON.parse(submission.ai_feedback);
        if (parsedData.criteria && Array.isArray(parsedData.criteria)) {
          // It's structured data
          setTeacherStructuredData(parsedData);
          setTeacherScore(String(parsedData.score));
          setTeacherFeedback(submission.ai_feedback);
        } else {
          // Old format - just copy as text
          setTeacherScore(String(submission.ai_score || 0));
          setTeacherFeedback(submission.ai_feedback);
        }
      } catch (e) {
        // Not JSON - old format
        setTeacherScore(String(submission.ai_score || 0));
        setTeacherFeedback(submission.ai_feedback);
      }
    } else if (submission && submission.ai_score !== null) {
      setTeacherScore(String(submission.ai_score));
    }
  };

  // Navigation helpers
  const buildNavigationUrl = (submissionId: number) => {
    const params = new URLSearchParams(searchParams);
    return `/submissions/${submissionId}/mark?${params.toString()}`;
  };

  const handlePreviousSubmission = () => {
    if (currentIndex > 0 && submissionsList.length > 0) {
      const prevId = submissionsList[currentIndex - 1];
      navigate(buildNavigationUrl(prevId));
    }
  };

  const handleNextSubmission = () => {
    if (currentIndex >= 0 && currentIndex < submissionsList.length - 1) {
      const nextId = submissionsList[currentIndex + 1];
      navigate(buildNavigationUrl(nextId));
    }
  };

  const handleSaveAndNext = async () => {
    if (!submission) return;

    try {
      // Save current grade
      await axios.patch(`/api/submissions/${submission.id}/`, {
        teacher_score: teacherScore ? Number(teacherScore) : null,
        teacher_feedback: teacherFeedback || null,
        status: 'marked'
      });

      // Navigate to next submission or back to list
      if (currentIndex >= 0 && currentIndex < submissionsList.length - 1) {
        handleNextSubmission();
      } else {
        // Last submission - return to submissions list with filters
        navigate(`/submissions?${searchParams.toString()}`);
      }
    } catch (err: any) {
      console.error('Error saving grade:', err);
      setError('Failed to save grade');
    }
  };

  const handleReturnToList = () => {
    navigate(`/submissions?${searchParams.toString()}`);
  };

  const handleReplaceFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !submission) return;
    setReplacing(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post<Submission>(
        `/api/submissions/${submission.id}/replace-file/`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      // Reload the page so the content viewer picks up the new file
      window.location.reload();
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to replace file';
      setError(msg);
    } finally {
      setReplacing(false);
      // Reset so the same file can be picked again if the user tries twice
      e.target.value = '';
    }
  };

  const handleReplaceText = async () => {
    if (!submission || !replacementText.trim()) return;
    setReplacingText(true);
    setError(null);
    try {
      await axios.post<Submission>(
        `/api/submissions/${submission.id}/replace-file/`,
        { text_content: replacementText },
        { headers: { 'Content-Type': 'application/json' } }
      );
      // Reload the page so the content viewer picks up the new text
      window.location.reload();
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to replace text';
      setError(msg);
    } finally {
      setReplacingText(false);
    }
  };

  // Whether this submission was a text/paste submission (vs file upload).
  const isTextSubmission = !!submission
    && (submission.content_type === 'text'
        || submission.assessment_submission_format === 'text');

  // Whether the current submission is still eligible for a learner-side file
  // replacement (i.e. status is pending and nothing has been marked yet).
  const canReplaceFile = !!submission
    && submission.status === 'pending'
    && submission.ai_score == null
    && submission.teacher_score == null
    && !submission.ai_feedback
    && !submission.teacher_feedback;

  // Check if navigation is available
  const hasNavigation = submissionsList.length > 0 && currentIndex >= 0;
  const hasPrevious = hasNavigation && currentIndex > 0;
  const hasNext = hasNavigation && currentIndex < submissionsList.length - 1;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!submission) {
    return (
      <Box>
        <Alert severity="error">Submission not found</Alert>
      </Box>
    );
  }

  return (
    <CodeSearchProvider>
      <Box>
        {/* Breadcrumbs and Navigation */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Breadcrumbs aria-label="breadcrumb">
            <MuiLink
              component="button"
              variant="body1"
              onClick={() => navigate('/dashboard')}
              sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              <Home fontSize="small" />
              Dashboard
            </MuiLink>
            <MuiLink
              component="button"
              variant="body1"
              onClick={handleReturnToList}
              sx={{ cursor: 'pointer' }}
            >
              Submissions
            </MuiLink>
            <Typography color="text.primary">
              {submission.learner_name ? `${submission.learner_name} - ${submission.assessment_title || 'Assessment'}` : `Submission ${submission.id}`}
            </Typography>
          </Breadcrumbs>

          {/* Navigation Controls - Teacher Only */}
          {userRole !== 'student' && hasNavigation && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                {currentIndex + 1} / {submissionsList.length}
              </Typography>
              <Tooltip title="Previous Submission">
                <span>
                  <IconButton
                    onClick={handlePreviousSubmission}
                    disabled={!hasPrevious}
                    size="small"
                  >
                    <NavigateBefore />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Next Submission">
                <span>
                  <IconButton
                    onClick={handleNextSubmission}
                    disabled={!hasNext}
                    size="small"
                  >
                    <NavigateNext />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Return to List">
                <IconButton
                  onClick={handleReturnToList}
                  size="small"
                >
                  <List />
                </IconButton>
              </Tooltip>
            </Stack>
          )}
        </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Two-Pane Layout */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          gap: 2,
          height: 'calc(100vh - 200px)',
          minHeight: '600px',
          position: 'relative'
        }}
      >
        {/* LEFT PANE: Student Work Viewer */}
        <Paper
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            minHeight: { xs: '400px', lg: 0 },
            maxWidth: markingPanelCollapsed ? '100%' : 'calc(100% - 420px)'
          }}
        >
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6" gutterBottom>
              <Description fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
              Student Submission
            </Typography>
            <Typography variant="body2" color="textSecondary">
              File: {submission.original_filename}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Submitted: {new Date(submission.submitted_at).toLocaleString()}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Status: <strong>{submission.status}</strong>
            </Typography>
            {canReplaceFile && !isTextSubmission && (
              <Box sx={{ mt: 1.5 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CloudUpload />}
                  disabled={replacing}
                  onClick={() => document.getElementById('replace-file-input')?.click()}
                >
                  {replacing ? 'Uploading…' : 'Replace File'}
                </Button>
                <input
                  id="replace-file-input"
                  type="file"
                  hidden
                  onChange={handleReplaceFile}
                />
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                  Uploaded the wrong file? Pick a new one. Only available until a teacher starts marking.
                </Typography>
              </Box>
            )}
            {canReplaceFile && isTextSubmission && (
              <Box sx={{ mt: 1.5 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  value={replacementText}
                  onChange={(e) => setReplacementText(e.target.value)}
                  placeholder="Paste your replacement text or code here…"
                  disabled={replacingText}
                  sx={{
                    fontFamily: 'monospace',
                    '& textarea': { fontFamily: 'monospace', fontSize: '0.85rem' },
                    mb: 1,
                  }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CloudUpload />}
                  disabled={replacingText || !replacementText.trim()}
                  onClick={handleReplaceText}
                >
                  {replacingText ? 'Replacing…' : 'Replace Text'}
                </Button>
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                  Edit your pasted text and click Replace. Only available until a teacher starts marking.
                </Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ flex: 1, overflow: 'auto', p: 2, maxWidth: '100%' }}>
            {/* Intelligent Content Viewer - automatically detects and displays content appropriately */}
            <SubmissionContentViewer submissionId={submission.id} />
          </Box>
        </Paper>

        {/* Collapse/Expand Toggle Button */}
        <IconButton
          onClick={() => setMarkingPanelCollapsed(!markingPanelCollapsed)}
          sx={{
            display: { xs: 'none', lg: 'block' },
            position: 'absolute',
            right: markingPanelCollapsed ? 0 : 400,
            top: '50%',
            transform: 'translateY(-50%)',
            bgcolor: 'grey.300',
            '&:hover': { bgcolor: 'grey.400' },
            borderRadius: '8px 0 0 8px',
            zIndex: 10,
            transition: 'right 0.3s'
          }}
        >
          {markingPanelCollapsed ? <ChevronLeft /> : <ChevronRight />}
        </IconButton>

        {/* RIGHT PANE: Marking Interface */}
        <Paper
          sx={{
            width: { xs: '100%', lg: markingPanelCollapsed ? 0 : 400, xl: markingPanelCollapsed ? 0 : 450 },
            opacity: markingPanelCollapsed ? 0 : 1,
            overflow: markingPanelCollapsed ? 'hidden' : 'auto',
            transition: 'all 0.3s',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              {userRole === 'student' ? 'Feedback' : 'Marking Panel'}
            </Typography>

            {/* Rubric Selection - Teacher Only */}
            {userRole !== 'student' && (
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <FormControl fullWidth>
                    <InputLabel>Select Rubric for AI Analysis</InputLabel>
                    <Select
                      value={selectedRubric}
                      onChange={(e) => setSelectedRubric(e.target.value as number | '')}
                      label="Select Rubric for AI Analysis"
                      disabled={marking}
                    >
                      <MenuItem value="">
                        <em>-- Select a Rubric --</em>
                      </MenuItem>
                      {rubrics.length === 0 && (
                        <MenuItem disabled>
                          <em>No rubrics available</em>
                        </MenuItem>
                      )}
                      {rubrics.map((rubric) => {
                        console.log('[SubmissionMarkingPage] Rendering rubric:', rubric.id, rubric.name);
                        return (
                          <MenuItem key={rubric.id} value={rubric.id}>
                            {rubric.description || rubric.name}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>

                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleMarkWithAI}
                    disabled={!selectedRubric || marking}
                    startIcon={marking ? <CircularProgress size={20} /> : <PlayArrow />}
                    sx={{ mt: 2 }}
                  >
                    {marking ? 'AI Analysis...' : 'Analyse with AI'}
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* AI Results Display - Teachers Only */}
            {userRole !== 'student' && (submission.ai_score !== null || aiResult) && (
              <Card sx={{ mb: 2, bgcolor: 'blue.50' }}>
                <CardContent>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    onClick={() => setAiResultExpanded(!aiResultExpanded)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <Typography variant="subtitle1" fontWeight="bold">
                      AI Analysis Results
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body1" fontWeight="bold">
                        Score: {submission.ai_score ?? aiResult?.ai_score ?? 'N/A'}
                      </Typography>
                      <IconButton size="small">
                        {aiResultExpanded ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    </Box>
                  </Box>

                  <Collapse in={aiResultExpanded}>
                    <Divider sx={{ my: 1 }} />
                    <StructuredFeedback
                      feedback={submission.ai_feedback ?? aiResult?.ai_feedback ?? 'No feedback available'}
                      totalScore={submission.ai_score ?? aiResult?.ai_score}
                    />
                  </Collapse>
                </CardContent>
              </Card>
            )}

            {/* Teacher's Feedback - Learners Only */}
            {userRole === 'student' && (submission.teacher_score !== null || submission.teacher_feedback) && (
              <Card sx={{ mb: 2, bgcolor: 'green.50' }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      Teacher's Feedback
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body1" fontWeight="bold">
                        Score: {submission.teacher_score ?? 'N/A'}
                      </Typography>
                      <CheckCircle color="success" fontSize="small" />
                    </Box>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <StructuredFeedback
                    feedback={submission.teacher_feedback ?? 'No feedback available'}
                    totalScore={submission.teacher_score ?? undefined}
                  />
                </CardContent>
              </Card>
            )}

            {/* Not Yet Marked - Learners Only */}
            {userRole === 'student' && !submission.teacher_score && !submission.teacher_feedback && (
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="body1" color="textSecondary" textAlign="center" py={2}>
                    Your work has been submitted and is awaiting teacher feedback.
                  </Typography>
                </CardContent>
              </Card>
            )}

            {/* Teacher Grade & Feedback - Teacher Edit Interface */}
            {userRole !== 'student' && (
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      Teacher's Final Grade & Feedback
                    </Typography>
                    {submission.teacher_score !== null && (
                      <CheckCircle color="success" fontSize="small" />
                    )}
                  </Box>

                  {submission.ai_score !== null && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleCopyFromAI}
                      fullWidth
                      sx={{ mb: 2 }}
                    >
                      Use Analysis
                    </Button>
                  )}

                  {teacherStructuredData ? (
                    <EditableStructuredFeedback
                      initialData={teacherStructuredData}
                      onDataChange={handleTeacherDataChange}
                    />
                  ) : (
                    <Box>
                      <TextField
                        label="Score"
                        type="number"
                        fullWidth
                        value={teacherScore}
                        onChange={(e) => setTeacherScore(e.target.value)}
                        sx={{ mb: 2 }}
                      />

                      <TextField
                        label="Feedback"
                        multiline
                        rows={6}
                        fullWidth
                        value={teacherFeedback}
                        onChange={(e) => setTeacherFeedback(e.target.value)}
                        sx={{ mb: 2 }}
                      />
                    </Box>
                  )}

                  {/* Save Buttons */}
                  <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                    <Button
                      variant="outlined"
                      color="success"
                      fullWidth
                      onClick={handleSaveTeacherGrade}
                      startIcon={<CheckCircle />}
                    >
                      Save
                    </Button>
                    {hasNext && (
                      <Button
                        variant="contained"
                        color="success"
                        fullWidth
                        onClick={handleSaveAndNext}
                        startIcon={<NavigateNext />}
                      >
                        Save & Next
                      </Button>
                    )}
                    {!hasNext && hasNavigation && (
                      <Button
                        variant="contained"
                        color="success"
                        fullWidth
                        onClick={handleSaveAndNext}
                        startIcon={<List />}
                      >
                        Save & Return
                      </Button>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            )}
          </Box>
        </Paper>
      </Box>
      </Box>
    </CodeSearchProvider>
  );
};

export default SubmissionMarkingPage;
